import * as React from "react"
import { captureError } from "../utils/analytics"

type State = { hasError: boolean }

/**
 * Captures React render/lifecycle errors to PostHog and shows a minimal recovery UI.
 */
export class PostHogErrorBoundary extends React.Component<React.PropsWithChildren, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    captureError(error, "react_error_boundary", { component_stack: info.componentStack })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
          <p className="text-lg font-bold text-gray-900">Something went wrong</p>
          <p className="text-sm text-gray-600 max-w-md">
            Please reload the page. If the problem continues, contact support.
          </p>
          <button
            type="button"
            className="px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-800"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
